import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Download, Plus, Upload } from "lucide-react";
import {
  api,
  countButtons,
  markupFromPage,
  pageFromOwner,
  type TreePage,
  type AutoReplyRule,
  type AutoReplyTest,
  type MatchType,
  type Source,
} from "@/lib/api";
import { useCan, useMe } from "@/lib/shell-context";
import { revealSoon } from "@/lib/utils";
import { TgHtml } from "@/components/rich-text";
import { MessageEditor, treeProblem } from "@/components/message-editor";
import { useConfirm } from "@/components/confirm";
import {
  Button,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Panel,
  Select,
  Toggle,
} from "@/components/primitives";

/**
 * Auto reply (spec §10, ticket 26).
 *
 * Exactly one rule answers a message: the first match in this list, top to
 * bottom. The order is the whole behaviour, so it is shown as a list you
 * reorder, and a test box shows which rule wins before a player finds out.
 * A reply never stops the message reaching CS; keyword replies post a
 * "🤖 Auto-replied" line in the Topic so nobody answers twice.
 */

const TRIGGERS: { value: MatchType; label: string; hint: string }[] = [
  {
    value: "contains",
    label: "Message contains",
    hint: "Any message with these words in it, ignoring capitals.",
  },
  {
    value: "exact",
    label: "Message is exactly",
    hint: "The whole message must be this, ignoring capitals. Use it for keyboard buttons.",
  },
  {
    value: "start",
    label: "Player taps Start",
    hint: "When someone opens the bot, including through a deep link.",
  },
  {
    value: "source",
    label: "Player came from a source",
    hint: "Welcome players from one influencer or campaign, on Start and on every message.",
  },
  {
    value: "first_contact",
    label: "New player's first message",
    hint: "Only the very first time a player writes or taps Start.",
  },
  {
    value: "fallback",
    label: "Anything else",
    hint: "Catches any typed message no rule above answered. Keep it at the bottom.",
  },
];

const triggerLabel = (t: MatchType) => TRIGGERS.find((x) => x.value === t)?.label ?? t;

interface Draft {
  id: number | null;
  name: string;
  matchType: MatchType;
  matchValue: string;
  sourceId: number | null;
  /** The reply: text, optional image or video, and its button tree. */
  page: TreePage;
  isActive: boolean;
}

function problemWith(d: Draft): string | null {
  if (!d.name.trim()) return "Name the rule; CS sees the name in the Topic.";
  if ((d.matchType === "exact" || d.matchType === "contains") && !d.matchValue.trim())
    return "Type the words this rule should match.";
  if (d.matchType === "source" && d.sourceId === null) return "Choose the source.";
  return treeProblem(d.page);
  return null;
}

export function AutoRepliesPage() {
  const { botId } = useParams();
  const canEdit = useCan("autoReplies", "manage");
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [touched, setTouched] = useState(false);

  const rules = useQuery({
    queryKey: ["auto-replies", botId],
    queryFn: () => api.get<AutoReplyRule[]>(`/bots/${botId}/auto-replies`),
  });
  const sources = useQuery({
    queryKey: ["sources", botId],
    queryFn: () => api.get<Source[]>(`/bots/${botId}/sources`),
  });
  const sourceName = (id: number | null) =>
    id === null ? null : (sources.data?.find((s) => s.id === id)?.name ?? "Unknown source");

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["auto-replies", botId] });

  const list = rules.data ?? [];

  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        name: d.name.trim(),
        matchType: d.matchType,
        matchValue:
          d.matchType === "exact" || d.matchType === "contains" ? d.matchValue.trim() : null,
        sourceId: d.sourceId,
        responseText: d.page.text.trim(),
        replyMarkup: markupFromPage(d.page),
        isActive: d.isActive,
      };
      if (d.id !== null) return api.patch(`/auto-replies/${d.id}`, body);
      // New rules go to the bottom, except a catch-all, which belongs there
      // anyway; the operator can move either.
      const last = list.at(-1)?.priority ?? 0;
      return api.post(`/bots/${botId}/auto-replies`, { ...body, priority: last + 10 });
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      setTouched(false);
    },
  });

  const patch = useMutation({
    mutationFn: ({ id, ...body }: { id: number; isActive?: boolean; priority?: number }) =>
      api.patch(`/auto-replies/${id}`, body),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/auto-replies/${id}`),
    onSuccess: invalidate,
  });

  /**
   * Moves a rule by rewriting priorities as 10, 20, 30… Only rows whose
   * number changes are saved, and the list is re-read afterwards.
   */
  const move = useMutation({
    mutationFn: async ({ index, by }: { index: number; by: -1 | 1 }) => {
      const order = [...list];
      const [rule] = order.splice(index, 1);
      order.splice(index + by, 0, rule!);
      const changes = order
        .map((r, i) => ({ id: r.id, priority: (i + 1) * 10, before: r.priority }))
        .filter((c) => c.priority !== c.before);
      for (const c of changes) await api.patch(`/auto-replies/${c.id}`, { priority: c.priority });
    },
    onSettled: invalidate,
  });

  const problem = draft ? problemWith(draft) : null;
  const trigger = draft ? TRIGGERS.find((t) => t.value === draft.matchType)! : null;

  return (
    <div className="grid grid-cols-1 gap-4">
      <TestBox botId={botId} sources={sources.data ?? []} />

      {draft && (
        <Panel id="rule-form" title={draft.id === null ? "New rule" : `Edit “${draft.name}”`}>
          <form
            className="grid gap-3 px-5 py-3"
            onSubmit={(e) => {
              e.preventDefault();
              setTouched(true);
              if (!problem) save.mutate(draft);
            }}
          >
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Name">
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Withdrawal times"
                  maxLength={80}
                />
              </Field>
              <Field label="When">
                <Select
                  className="w-full"
                  value={draft.matchType}
                  onChange={(e) => setDraft({ ...draft, matchType: e.target.value as MatchType })}
                >
                  {TRIGGERS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <p className="-mt-1 text-muted-foreground">{trigger?.hint}</p>

            {(draft.matchType === "exact" || draft.matchType === "contains") && (
              <Field label={draft.matchType === "exact" ? "Exact message" : "Words"}>
                <Input
                  value={draft.matchValue}
                  onChange={(e) => setDraft({ ...draft, matchValue: e.target.value })}
                  placeholder={draft.matchType === "exact" ? "Deposit" : "withdraw"}
                  maxLength={200}
                />
              </Field>
            )}

            <Field
              label={draft.matchType === "source" ? "Source" : "Only for players from (optional)"}
            >
              <Select
                className="w-full md:max-w-sm"
                value={draft.sourceId ?? ""}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    sourceId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">
                  {draft.matchType === "source" ? "Choose a source" : "Everyone"}
                </option>
                {sources.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="grid gap-1.5">
              <div className="font-medium">Reply</div>
              <div className="rounded-md border border-border p-3">
                <MessageEditor
                  value={draft.page}
                  onChange={(page) => setDraft({ ...draft, page })}
                  textPlaceholder="Withdrawals usually arrive within 24 hours."
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <Toggle
                  label="Rule is on"
                  checked={draft.isActive}
                  onChange={(isActive) => setDraft({ ...draft, isActive })}
                />
                {draft.isActive ? "On" : "Off"}
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? "Saving" : draft.id === null ? "Add rule" : "Save changes"}
              </Button>
              <Button
                type="button"
                variant="quiet"
                onClick={() => {
                  setDraft(null);
                  setTouched(false);
                }}
              >
                Cancel
              </Button>
              {touched && problem && <span className="text-destructive">{problem}</span>}
            </div>
            {save.error && <ErrorNote error={save.error} />}
          </form>
        </Panel>
      )}

      <Panel
        title="Rules, first match wins"
        action={
          canEdit &&
          !draft && (
            <span className="flex flex-wrap items-center justify-end gap-1.5">
              <RuleTransfer botId={botId!} onImported={invalidate} />
              <Button
                size="sm"
                onClick={() =>
                  setDraft({
                    id: null,
                    name: "",
                    matchType: "contains",
                    matchValue: "",
                    sourceId: null,
                    page: { text: "", buttons: [] },
                    isActive: true,
                  })
                }
              >
                <Plus className="size-4" /> New rule
              </Button>
            </span>
          )
        }
      >
        {rules.isLoading ? (
          <Loading />
        ) : rules.error ? (
          <div className="p-3.5">
            <ErrorNote error={rules.error} />
          </div>
        ) : list.length === 0 ? (
          <Empty
            title="No rules yet"
            hint="Start with a welcome for Start, then answers to the questions CS gets most."
          />
        ) : (
          <ol className="divide-y divide-border/60">
            {list.map((rule, index) => (
              <li
                key={rule.id}
                className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 px-5 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
              >
                <span className="mono pt-0.5 text-muted-foreground">{index + 1}</span>
                <div className={rule.isActive ? "min-w-0" : "min-w-0 opacity-60"}>
                  <div className="font-medium">{rule.name}</div>
                  <div className="text-muted-foreground">
                    {triggerLabel(rule.matchType)}
                    {rule.matchValue && (
                      <>
                        {" "}
                        <span className="mono text-foreground">“{rule.matchValue}”</span>
                      </>
                    )}
                    {rule.sourceId !== null && <> · {sourceName(rule.sourceId)}</>}
                    {(() => {
                      const page = pageFromOwner(rule.responseText, rule.replyMarkup);
                      const n = countButtons(page);
                      return (
                        <>
                          {page.media && <> · {page.media.kind === "video" ? "video" : "image"}</>}
                          {n > 0 && (
                            <>
                              {" "}
                              · {n} {n === 1 ? "button" : "buttons"}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <TgHtml
                    className="mt-1 line-clamp-2"
                    html={pageFromOwner(rule.responseText, rule.replyMarkup).text}
                  />
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-1 sm:self-center">
                  <Toggle
                    label={`${rule.name} is ${rule.isActive ? "on" : "off"}`}
                    checked={rule.isActive}
                    disabled={!canEdit || patch.isPending}
                    onChange={(isActive) => patch.mutate({ id: rule.id, isActive })}
                  />
                  {canEdit && (
                    <>
                      <MoveButton
                        label="Move up"
                        disabled={index === 0 || move.isPending}
                        onClick={() => move.mutate({ index, by: -1 })}
                      >
                        <ArrowUp />
                      </MoveButton>
                      <MoveButton
                        label="Move down"
                        disabled={index === list.length - 1 || move.isPending}
                        onClick={() => move.mutate({ index, by: 1 })}
                      >
                        <ArrowDown />
                      </MoveButton>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          revealSoon("rule-form");
                          setDraft({
                            id: rule.id,
                            name: rule.name,
                            matchType: rule.matchType,
                            matchValue: rule.matchValue ?? "",
                            sourceId: rule.sourceId,
                            page: pageFromOwner(rule.responseText, rule.replyMarkup),
                            isActive: rule.isActive,
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Delete the rule “${rule.name}”?`,
                              confirmLabel: "Delete rule",
                              destructive: true,
                            })
                          )
                            remove.mutate(rule.id);
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
        {(patch.error || move.error || remove.error) && (
          <div className="p-3.5">
            <ErrorNote error={patch.error ?? move.error ?? remove.error} />
          </div>
        )}
        <p className="border-t border-border px-5 py-2.5 text-muted-foreground">
          The message still reaches CS whatever the bot replies. On Start, only the Start, source
          and new-player rules are checked.
        </p>
      </Panel>
    </div>
  );
}

/** "What would the bot say to this?" — answered by the server's own matcher. */
function TestBox({ botId, sources }: { botId: string | undefined; sources: Source[] }) {
  const [text, setText] = useState("");
  const [firstContact, setFirstContact] = useState(false);
  const [sourceId, setSourceId] = useState<number | null>(null);

  const test = useMutation({
    mutationFn: () =>
      api.post<AutoReplyTest>(`/bots/${botId}/auto-replies/test`, {
        text,
        isFirstContact: firstContact,
        latestSourceId: sourceId,
      }),
  });

  return (
    <Panel title="Try a message">
      <form
        className="grid gap-3 px-5 py-3 md:grid-cols-[minmax(0,1fr)_200px_auto] md:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          test.mutate();
        }}
      >
        <Field label="Player writes">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="how long does a withdrawal take?"
          />
        </Field>
        <Field label="Player came from">
          <Select
            className="w-full"
            value={sourceId ?? ""}
            onChange={(e) => setSourceId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Any source</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" disabled={test.isPending}>
          Test
        </Button>
        <label className="flex items-center gap-2 md:col-span-full">
          <input
            type="checkbox"
            checked={firstContact}
            onChange={(e) => setFirstContact(e.target.checked)}
          />
          This is the player's first message
        </label>
      </form>
      {test.data && (
        <div className="border-t border-border px-5 py-3">
          {test.data.matched ? (
            <>
              <div className="text-muted-foreground">
                Answered by{" "}
                <span className="font-medium text-foreground">{test.data.ruleName}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap [overflow-wrap:anywhere]">
                {test.data.response}
              </p>
            </>
          ) : (
            <p className="text-muted-foreground">
              No rule answers this. The message goes to CS with no automatic reply.
            </p>
          )}
        </div>
      )}
      {test.error && (
        <div className="px-5 pb-3">
          <ErrorNote error={test.error} />
        </div>
      )}
    </Panel>
  );
}

function MoveButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 [&_svg]:size-4"
    >
      {children}
    </button>
  );
}

/**
 * Export and import of the whole rule set (decision 54). Super admin only —
 * the server refuses anyone else, and a file holds every reply word for word.
 * An import adds the file's rules after the existing ones; nothing is replaced.
 */
function RuleTransfer({ botId, onImported }: { botId: string; onImported: () => void }) {
  const me = useMe();
  const confirm = useConfirm();
  const [note, setNote] = useState<string | null>(null);

  const exportRules = useMutation({
    mutationFn: () => api.get<{ bot: string | null }>(`/bots/${botId}/auto-replies/export`),
    onSuccess: (file) => {
      const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `auto-replies-${file.bot ?? botId}-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  });

  const importRules = useMutation({
    mutationFn: (file: unknown) =>
      api.post<{ added: number }>(`/bots/${botId}/auto-replies/import`, file),
    onSuccess: (result) => {
      setNote(`Added ${result.added} ${result.added === 1 ? "rule" : "rules"}.`);
      onImported();
    },
  });

  if (me.role !== "super_admin") return null;

  async function choose(event: React.ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const chosen = input.files?.[0];
    input.value = "";
    if (!chosen) return;
    setNote(null);

    let file: { kind?: unknown; bot?: unknown; rules?: unknown[] };
    try {
      file = JSON.parse(await chosen.text()) as typeof file;
    } catch {
      return setNote("That file is not an auto-reply export.");
    }
    const count = Array.isArray(file.rules) ? file.rules.length : 0;
    if (file.kind !== "kickwin.auto-replies" || count === 0) {
      return setNote("That file is not an auto-reply export, or it has no rules.");
    }
    if (
      await confirm({
        title: `Add ${count} ${count === 1 ? "rule" : "rules"} to this bot?`,
        description:
          (typeof file.bot === "string" ? `Exported from @${file.bot}. ` : "") +
          "They go after your current rules, above any catch-all. Nothing is replaced or removed.",
        confirmLabel: "Import",
      })
    )
      importRules.mutate(file);
  }

  return (
    <>
      <Button
        size="sm"
        variant="quiet"
        disabled={exportRules.isPending}
        onClick={() => exportRules.mutate()}
      >
        <Download className="size-4" /> Export
      </Button>
      <label className="inline-flex">
        <input type="file" accept="application/json,.json" className="sr-only" onChange={choose} />
        <span className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground">
          <Upload className="size-4" /> {importRules.isPending ? "Importing…" : "Import"}
        </span>
      </label>
      {(note || exportRules.error || importRules.error) && (
        <span className="basis-full text-right text-sm">
          {note ?? <ErrorNote error={exportRules.error ?? importRules.error} />}
        </span>
      )}
    </>
  );
}
